import { queryOptions, skipToken } from "@tanstack/vue-query";
import { getFolderNavByShortcutId } from "ls/api/cloud/fs";
import { getUserShortcutPermissions } from "ls/api/cloud/permissions";
import { getFirstPage, getPassword, getPrototypeInfo, getPrototypePages, getShortPrototypeInfo } from "ls/api/cloud/prototype";
import { getPluginInfo, getPrettyUrls, getRedirects } from "ls/api/cloud/sites";
import { STALE } from "ls/queries/staleTime";

export const queries = {
  files: () => ["file"] as const,
  file: (shortcut: string) => [...queries.files(), shortcut.toLowerCase()] as const,
  location: (shortcut: string | undefined) =>
    queryOptions({
      queryKey: [...queries.file(shortcut ?? ""), "location"] as const,
      queryFn: shortcut
        ? ({ signal }) => getFolderNavByShortcutId(shortcut.toLowerCase(), { signal })
        : skipToken,
      staleTime: STALE.SECONDS.FIFTEEN,
    }),
  info: (shortcut: string | undefined) =>
    queryOptions({
      queryKey: [...queries.file(shortcut ?? ""), "info"] as const,
      queryFn: shortcut ? ({ signal }) => getPrototypeInfo(shortcut.toLowerCase(), { signal, ignoreRedirect: true }) : skipToken,
      staleTime: STALE.SECONDS.FIFTEEN,
    }),
  permissions: (shortcut: string) =>
    queryOptions({
      queryKey: [...queries.file(shortcut), "permissions"] as const,
      queryFn: ({ signal }) => getUserShortcutPermissions(shortcut.toLowerCase(), { signal }),
      staleTime: STALE.SECONDS.FIFTEEN,
    }),
  shortInfo: (shortcut: string, options: { accessCode?: string } = {}) =>
    queryOptions({
      queryKey: [...queries.file(shortcut), "short-info", options] as const,
      queryFn: ({ signal }) => getShortPrototypeInfo(shortcut.toLowerCase(), options.accessCode, { signal }),
    }),
  password: (shortcut: string) =>
    queryOptions({
      queryKey: [...queries.file(shortcut), "password"] as const,
      queryFn: ({ signal }) => getPassword(shortcut.toLowerCase(), { signal }),
    }),
  pages: (shortcut: string) =>
    queryOptions({
      queryKey: [...queries.file(shortcut), "pages"] as const,
      queryFn: ({ signal }) => getPrototypePages(shortcut.toLowerCase(), { signal }),
    }),
  firstPage: (shortcut: string) =>
    queryOptions({
      queryKey: [...queries.file(shortcut), "firstPage"] as const,
      queryFn: ({ signal }) => getFirstPage(shortcut.toLowerCase(), { signal }),
    }),
  plugins: (shortcut: string) =>
    queryOptions({
      queryKey: [...queries.file(shortcut), "plugins"] as const,
      queryFn: ({ signal }) => getPluginInfo(shortcut.toLowerCase(), { signal }),
    }),
  redirects: (shortcut: string) =>
    queryOptions({
      queryKey: [...queries.file(shortcut), "redirects"] as const,
      queryFn: ({ signal }) => getRedirects(shortcut.toLowerCase(), { signal }),
    }),
  prettyUrls: (shortcut: string) =>
    queryOptions({
      queryKey: [...queries.file(shortcut), "pretty-urls"] as const,
      queryFn: ({ signal }) => getPrettyUrls(shortcut.toLowerCase(), { signal }),
    }),
};
